import { DateTime } from 'luxon';
import { CoachHistoryRoute } from './coach-history';

// The monthly bars of the coach billing page's "Clients Over Time" card (#6768,
// Phase 3 of #4456). Each bar is the most clients the coach had at any moment of
// that month. The rule and a worked example: design-docs/coach-billing-analytics.md,
// "Phase 3 — the coach's view".

/** The fields of a history row the chart reads. A row without both counts (a
 *  charge row can lack them) takes no part. */
export interface CoachHistoryChartRow {
  /** When the change happened, ISO 8601 in UTC. */
  at_utc: string;
  route: CoachHistoryRoute | string;
  clientsAwake?: number;
  clientsAsleep?: number;
  /** Stores: the plan's client limit at that moment. */
  tierCap?: number;
}

export interface MonthlyClientBar {
  /** The first moment of the month, in the zone the bars were built for. */
  monthStart: DateTime;
  active: number;
  asleep: number;
  /** The plan limit in force at the month's peak, for a store route only. */
  cap?: number;
  /** False for a month before the first row: nothing is known, so it is drawn empty. */
  hasData: boolean;
}

/** One state the chart can pick: the counts after a row, and the row's plan. */
interface CountedState {
  atMs: number;
  active: number;
  asleep: number;
  route: string;
  tierCap?: number;
}

const kStoreRoutes = new Set<string>([CoachHistoryRoute.Apple, CoachHistoryRoute.GooglePlay]);

function isCount(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0;
}

/** The rows that carry both counts and a readable time, oldest first. Rows with
 *  the same time keep the order they came in, which is the order they were saved. */
function countedStates(rows: readonly CoachHistoryChartRow[]): CountedState[] {
  const states: CountedState[] = [];
  // for each row with a readable time and both counts: keep its state
  for (const row of rows) {
    if (!isCount(row?.clientsAwake) || !isCount(row?.clientsAsleep)) {
      continue;
    }
    const at = DateTime.fromISO(row.at_utc, { zone: 'utc' });
    if (!at.isValid) {
      continue;
    }
    states.push({
      atMs: at.toMillis(),
      active: row.clientsAwake,
      asleep: row.clientsAsleep,
      route: row.route,
      tierCap: isCount(row.tierCap) ? row.tierCap : undefined,
    });
  }
  // stable sort, so rows saved at the same moment keep their order
  return states
    .map((state, index) => ({ state, index }))
    .sort((a, b) => a.state.atMs - b.state.atMs || a.index - b.index)
    .map(entry => entry.state);
}

/**
 * One bar per month for the `months` months ending with the month `nowUtc` falls
 * in (that month included), in time zone `zone`.
 *
 * For each month, the candidates are the state carried in from before the month
 * plus the state after every row inside it. The bar is the candidate with the
 * highest total; on a tie, the latest of the tied candidates. A month with no
 * candidate (before the first row) is empty, with `hasData: false`.
 */
export function buildMonthlyClientBars(
  rows: readonly CoachHistoryChartRow[],
  months: number,
  nowUtc: DateTime,
  zone: string,
): MonthlyClientBar[] {
  const states = countedStates(rows);
  const currentMonthStart = nowUtc.setZone(zone).startOf('month');
  const bars: MonthlyClientBar[] = [];
  let nextState = 0;
  let carried: CountedState | undefined;

  const firstMonthStart = currentMonthStart.minus({ months: months - 1 });
  // carry in everything before the first month shown
  while (nextState < states.length && states[nextState].atMs < firstMonthStart.toMillis()) {
    carried = states[nextState];
    nextState++;
  }

  // for each month, oldest first: pick the fullest state among the carried one and the month's own
  for (let offset = months - 1; offset >= 0; offset--) {
    const monthStart = currentMonthStart.minus({ months: offset });
    const monthEndMs = monthStart.plus({ months: 1 }).toMillis();
    let best = carried;
    while (nextState < states.length && states[nextState].atMs < monthEndMs) {
      const state = states[nextState];
      // if this state is at least as full, it wins, so the latest of tied states is kept
      if (!best || state.active + state.asleep >= best.active + best.asleep) {
        best = state;
      }
      carried = state;
      nextState++;
    }
    if (!best) {
      bars.push({ monthStart, active: 0, asleep: 0, hasData: false });
      continue;
    }
    const bar: MonthlyClientBar = { monthStart, active: best.active, asleep: best.asleep, hasData: true };
    if (kStoreRoutes.has(best.route) && best.tierCap !== undefined) {
      bar.cap = best.tierCap;
    }
    bars.push(bar);
  }
  return bars;
}

/** The plan limit of the newest month that has one, for the chart's caption. */
export function newestBarCap(bars: readonly MonthlyClientBar[]): number | undefined {
  // for each month, newest first: the first limit found is the one in force now
  for (let i = bars.length - 1; i >= 0; i--) {
    if (bars[i].cap !== undefined) {
      return bars[i].cap;
    }
  }
  return undefined;
}
